/**
 * Voice AI — Agent Service
 * Owns all agent CRUD API calls. Components call this service
 * instead of making direct fetch calls.
 */

import { authService } from './auth-service.js';

const API_BASE = '/voice/php/api';

class AgentService {

  /**
   * List all agents with optional filters.
   * @param {Object} opts - { search, status, sort, sort_dir }
   * @returns {Promise<Object>} { success, agents[], stats, source }
   */
  async listAgents(opts = {}, { signal } = {}) {
    const params = new URLSearchParams({ ujid: '0' });
    if (opts.search) params.set('search', opts.search);
    if (opts.status) params.set('status', opts.status);
    if (opts.sort) params.set('sort', opts.sort);
    if (opts.sort_dir) params.set('sort_dir', opts.sort_dir);

    const fetchOpts = {};
    if (signal) fetchOpts.signal = signal;
    const res = await authService.apiFetch(`${API_BASE}/agents.php?${params}`, fetchOpts);
    return res.json();
  }

  /**
   * Get a single agent by ID.
   * @param {number} id
   * @returns {Promise<Object>} { success, agent }
   */
  async getAgent(id) {
    const res = await authService.apiFetch(`${API_BASE}/agents.php?id=${id}`);
    return res.json();
  }

  /**
   * Create a new agent.
   * @param {Object} data - { name, ...optional fields }
   * @returns {Promise<Object>} { success, agent, id }
   */
  async createAgent(data) {
    const res = await authService.apiFetch(`${API_BASE}/agents.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return res.json();
  }

  /**
   * Update an existing agent.
   * @param {number} id
   * @param {Object} data - Fields to update (snake_case)
   * @returns {Promise<Object>} { success, agent }
   */
  async updateAgent(id, data) {
    const res = await authService.apiFetch(`${API_BASE}/agents.php?id=${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return res.json();
  }

  /**
   * Delete an agent.
   * @param {number} id
   * @returns {Promise<Object>} { success }
   */
  async deleteAgent(id) {
    const res = await authService.apiFetch(`${API_BASE}/agents.php?id=${id}`, {
      method: 'DELETE',
    });
    return res.json();
  }

  /**
   * Transform API snake_case agent to camelCase for frontend use.
   */
  transformAgent(a) {
    return {
      id: a.id,
      name: a.name,
      description: a.description || '',
      status: a.status,
      language: a.language || 'en-US',
      voiceName: a.voice_name || '',
      color: a.color || 'orange',
      systemPrompt: a.system_prompt || '',
      tone: a.tone || 'professional',
      greeting: a.greeting || '',
      greetingUseName: a.greeting_use_name ?? 1,
      greetingLlmFreestyle: a.greeting_llm_freestyle ?? 0,
      ttsWordReplacements: a.tts_word_replacements || [],
      afterHoursMessage: a.after_hours_message || '',
      businessHours: this._ensureBusinessHours(a.business_hours),
      maxDuration: a.max_duration || 600,
      transferNumber: a.transfer_number || '',
      interruptionHandling: a.interruption_handling || 'allow',
      pipelineOverrides: a.pipeline_overrides || {},
      llmProviderKey: a.llm_provider_key || null,
      sttProviderKey: a.stt_provider_key || null,
      ttsProviderKey: a.tts_provider_key || null,
      contextualLlmProviderKey: a.contextual_llm_provider_key || null,
      voiceSettings: a.voice_settings || null,
      isAgentOne: a.is_agent_one === 1,
      guardrails: a.guardrails || null,
      aiSetup: a.ai_setup || null,
      answerDelay: a.answer_delay ?? 0,
      totalCalls: a.total_calls || 0,
      successRate: a.success_rate || 0,
      avgRating: a.avg_rating || 0,
      lastActiveAt: a.last_active_at,
      recentCalls: a.recentCalls || [],
      activity: a.activity || [],
    };
  }

  /**
   * Ensure businessHours always has all 7 days with defaults.
   */
  _ensureBusinessHours(bh) {
    const defaults = {
      Mon: { enabled: true, start: '09:00', end: '17:00' },
      Tue: { enabled: true, start: '09:00', end: '17:00' },
      Wed: { enabled: true, start: '09:00', end: '17:00' },
      Thu: { enabled: true, start: '09:00', end: '17:00' },
      Fri: { enabled: true, start: '09:00', end: '17:00' },
      Sat: { enabled: false, start: '09:00', end: '17:00' },
      Sun: { enabled: false, start: '09:00', end: '17:00' },
    };
    if (!bh || typeof bh !== 'object' || Object.keys(bh).length === 0) return defaults;
    // Fill any missing days
    for (const day of Object.keys(defaults)) {
      if (!bh[day]) bh[day] = defaults[day];
    }
    return bh;
  }

  /**
   * Convert camelCase agent back to snake_case for API.
   */
  toSnakeCase(agent) {
    return {
      name: agent.name,
      description: agent.description,
      status: agent.status,
      language: agent.language || 'en-US',
      voice_name: agent.voiceName || '',
      color: agent.color,
      system_prompt: agent.systemPrompt,
      tone: agent.tone,
      greeting: agent.greeting,
      greeting_use_name: agent.greetingUseName ?? 1,
      greeting_llm_freestyle: agent.greetingLlmFreestyle ?? 0,
      tts_word_replacements: agent.ttsWordReplacements || [],
      after_hours_message: agent.afterHoursMessage,
      business_hours: agent.businessHours,
      max_duration: agent.maxDuration,
      transfer_number: agent.transferNumber,
      interruption_handling: agent.interruptionHandling,
      pipeline_overrides: agent.pipelineOverrides || null,
      llm_provider_key: agent.llmProviderKey || null,
      stt_provider_key: agent.sttProviderKey || null,
      tts_provider_key: agent.ttsProviderKey || null,
      contextual_llm_provider_key: agent.contextualLlmProviderKey || null,
      voice_settings: agent.voiceSettings || null,
      is_agent_one: agent.isAgentOne ? 1 : 0,
      guardrails: agent.guardrails || null,
      ai_setup: agent.aiSetup || null,
      answer_delay: agent.answerDelay ?? 0,
    };
  }

  /**
   * Load call activity for a specific agent (paginated).
   * @param {number} agentId
   * @param {Object} opts - { page, limit }
   * @returns {Promise<Object>} { success, calls[], pagination, stats }
   */
  async loadAgentActivity(agentId, opts = {}) {
    const params = new URLSearchParams({ agent_id: String(agentId) });
    if (opts.page) params.set('page', String(opts.page));
    if (opts.limit) params.set('limit', String(opts.limit));

    const res = await authService.apiFetch(`${API_BASE}/agent-activity.php?${params}`);
    return res.json();
  }

  /**
   * Load full call detail (transcripts + tool calls).
   * @param {number} agentId
   * @param {number} callId
   * @returns {Promise<Object>} { success, call, transcripts[], toolCalls[] }
   */
  async loadCallDetail(agentId, callId) {
    const params = new URLSearchParams({ agent_id: String(agentId), call_id: String(callId) });
    const res = await authService.apiFetch(`${API_BASE}/agent-activity.php?${params}`);
    return res.json();
  }

  /**
   * Load timeline events for a specific agent.
   * @param {number} agentId
   * @param {Object} opts - { page, limit }
   * @returns {Promise<Object>} { success, events[], pagination }
   */
  async loadAgentTimeline(agentId, opts = {}) {
    const params = new URLSearchParams({ agent_id: String(agentId) });
    if (opts.page) params.set('page', String(opts.page));
    if (opts.limit) params.set('limit', String(opts.limit));

    const res = await authService.apiFetch(`${API_BASE}/agent-timeline.php?${params}`);
    return res.json();
  }

  /**
   * Relative time display helper.
   */
  relativeTime(dateStr) {
    if (!dateStr) return 'Never';
    const now = new Date();
    const then = new Date(dateStr);
    const diffMs = now - then;
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'Just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    const diffDay = Math.floor(diffHr / 24);
    if (diffDay < 30) return `${diffDay}d ago`;
    return `${Math.floor(diffDay / 30)}mo ago`;
  }
}

export const agentService = new AgentService();
